/**
 * BL-182 Gate 11 — Manual override audit helpers.
 *
 * Pure functions (no Supabase imports) so they can be unit-tested without
 * mocking the DB client.  The caller is responsible for persisting the
 * audit entry returned by buildOverrideAuditEntry to
 * invoice_line_item_overrides.
 */

/**
 * Returns true when a line item carries a hand-set price override.
 *
 * @param {object|null} lineItem  Row from invoice_line_items (or a test stub).
 * @returns {boolean}
 */
export function isLineItemOverridden(lineItem) {
    return Boolean(lineItem?.is_overridden);
}

/**
 * Build an audit-entry payload ready for INSERT into
 * invoice_line_item_overrides.  Does not persist — call supabaseClient
 * .from('invoice_line_item_overrides').insert(entry) afterwards.
 *
 * @param {object} params
 * @param {string} params.orgId          org_id for the tenant.
 * @param {string} params.lineItemId     invoice_line_items.id being overridden.
 * @param {string|null} [params.invoiceId]  invoice_id the line item belongs to.
 * @param {string} params.overriddenBy   Display name or user identifier of who
 *                                       applied the override (free text —
 *                                       BL-103 role model is still parked).
 * @param {number|null} [params.oldValue]  Previous amount before the override.
 * @param {number|null} [params.newValue]  New hand-set amount.
 * @param {string|null} [params.reason]    Free-text reason for the override.
 * @returns {{
 *   org_id: string,
 *   line_item_id: string,
 *   invoice_id: string|null,
 *   overridden_by: string,
 *   overridden_at: string,
 *   old_value: number|null,
 *   new_value: number|null,
 *   override_reason: string|null
 * }}
 */
export function buildOverrideAuditEntry({
    orgId,
    lineItemId,
    invoiceId = null,
    overriddenBy,
    oldValue = null,
    newValue = null,
    reason = null,
}) {
    if (!orgId) throw new Error('buildOverrideAuditEntry: orgId is required');
    if (!lineItemId) throw new Error('buildOverrideAuditEntry: lineItemId is required');
    if (!overriddenBy) throw new Error('buildOverrideAuditEntry: overriddenBy is required');

    return {
        org_id:          orgId,
        line_item_id:    lineItemId,
        invoice_id:      invoiceId,
        overridden_by:   overriddenBy,
        overridden_at:   new Date().toISOString(),
        old_value:       oldValue,
        new_value:       newValue,
        override_reason: reason || null,
    };
}
